import { Component, computed, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { youtubeIdFromUrl } from '@fcom/core/utils/utils';
import { StaticPicture } from '@fcom/core-api';
import { ModalButtons } from '@fcom/ui-components/components/modal/interfaces';

@Component({
  selector: 'fin-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  standalone: false,
})
export class VideoComponent {
  private sanitizer = inject(DomSanitizer);

  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly videoUrl = input<string>();
  readonly videoTitle = input<string>();
  readonly showPopup = input(false);
  readonly noMargin = input(false);

  readonly iframe = viewChild<ElementRef>('videoIframe');
  readonly videoWrapper = viewChild<ElementRef>('videoWrapper');

  readonly iframeVisible = signal(false);
  readonly previewVisible = computed(() => {
    return !this.iframeVisible();
  });

  private readonly videoId = computed(() => {
    const url = this.videoUrl();
    return url ? youtubeIdFromUrl(url) : null;
  });

  readonly videoFinalUrl = computed<SafeResourceUrl | null>(() => {
    const videoId = this.videoId();
    if (!videoId) {
      return null;
    }
    const url =
      `https://www.youtube.com/embed/${videoId}` +
      '?modestbranding=1' + // Show as little YouTube branding as possible
      '&rel=0' + // Show related videos out of same channel only
      '&autoplay=1'; // Autoplay when loaded (as only loaded on click interaction)
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  readonly youtubePreviewPicture = computed<StaticPicture | null>(() => {
    const videoId = this.videoId();
    if (!videoId) {
      return null;
    }
    return {
      thumbnailSrc: `//i.ytimg.com/vi/${videoId}/default.jpg`,
      fullSizeUrl: `//i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    };
  });

  playVideo(): void {
    this.iframeVisible.set(true);
  }
}
